import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import UploadCloud from '../../assets/Icons/upload-cloud';
import { ReactNode, useState } from 'react';
import Dots from '../../assets/Icons/dots-three';

type UploadDocumentProps = {
  title: string;
  icon?: ReactNode;
  square?: boolean;
  disabled?: boolean;
  actions?: {
    icon: JSX.Element;
    text: string;
    onClick?: () => void;
  }[];
  onChange?: (file?: File) => void | Promise<void>;
};

const generateRandomString = () => {
  return Math.random().toString(36).substring(7);
};

export const UploadDocument = ({
  title,
  icon,
  square,
  disabled,
  actions,
  onChange,
}: UploadDocumentProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [openActions, setOpenActions] = useState<null | HTMLElement>(null);

  const fileId = 'upload-' + generateRandomString();

  const onDragEvent = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onFileDrop = async (e: React.DragEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (!!onChange) {
      setIsUploading(true);
      await onChange(e.dataTransfer.files[0]);
      setIsUploading(false);
    }
  };

  const handleOpenActions = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenActions(event.currentTarget);
  };

  const handleCloseActions = () => {
    setOpenActions(null);
  };

  const actionsButton = !!actions && (
    <Box>
      <IconButton
        aria-label='close'
        sx={{
          padding: 0,
          color: theme => theme.palette.grey[500],
        }}
        onClick={handleOpenActions}
      >
        <Dots />
      </IconButton>

      <Menu
        id='basic-menu'
        anchorEl={openActions}
        open={Boolean(openActions)}
        onClose={handleCloseActions}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {actions?.map(action => {
          return (
            <MenuItem onClick={action.onClick}>
              <ListItemIcon>{action.icon}</ListItemIcon>
              <ListItemText>{action.text}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );

  return (
    <Box onDragEnter={onDragEvent} onDragOver={onDragEvent} onDrop={onFileDrop}>
      <label htmlFor={fileId}>
        <Paper
          variant='outlined'
          sx={{
            cursor: !disabled ? 'pointer' : 'auto',
            width: '100%',
            padding: '12px 24px',
            borderStyle: 'dashed',
            borderRadius: !!square ? '4px' : '16px',
            boxSizing: 'border-box',
          }}
        >
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            display='flex'
          >
            <Stack
              width='40%'
              display='flex'
              direction='row'
              gap='16px'
              alignItems='center'
            >
              <Box>{icon || <UploadCloud />}</Box>
              <Typography
                sx={{
                  width: '100%',
                  color: 'var(--Primary-Text, #202A2D)',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: 'normal',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {title}
              </Typography>
            </Stack>
            <Stack direction='row' alignItems='center' gap='10px'>
              {isUploading ? (
                <CircularProgress size={20} />
              ) : !disabled ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#1F76C7',
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                    }}
                  >
                    Click to upload
                  </Typography>
                  <Typography
                    sx={{
                      color: '#202A2D',
                      fontFamily: 'Inter',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                    }}
                  >
                    or drag and drop
                  </Typography>
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: '#655C68',
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                  }}
                >
                  Pending upload
                </Typography>
              )}

              {actionsButton}
            </Stack>
          </Stack>
        </Paper>
      </label>
      <input
        type='file'
        id={fileId}
        multiple={false}
        hidden
        disabled={!!disabled || !!isUploading}
        onChange={e => !!onChange && onChange(e.target.files?.[0])}
      />
    </Box>
  );
};
